import { useId } from "react";
import { IonInput } from "@ionic/react";
import { type AxiosError } from "axios";
import { translate } from "components/string-table";
import { App, Colors, EmailLoginErrorProps, ErrorLogin, Icon } from "main";

export type BuildProfile = "local" | "staging" | "prod";

type EmailNonReadOnly = {
  app: App;
  buildProfile?: BuildProfile;
  email: string;
  error?: AxiosError<EmailLoginErrorProps>;
  onEmailInput: (email: string) => void;
  readOnly: false;
  platform: string;
  provider?: "cognito" | "google";
};

type EmailReadOnly = {
  app: App;
  buildProfile?: BuildProfile;
  email: string;
  error?: AxiosError<EmailLoginErrorProps>;
  onEmailEdit: (email: string) => void;
  readOnly: true;
  platform: string;
  provider?: "cognito" | "google";
};

type EmailFieldProps = EmailNonReadOnly | EmailReadOnly;

export function EmailField(props: EmailFieldProps) {
  const id = useId();
  const { app, buildProfile, email, error, platform, provider, readOnly } = props;
  const onEmailChange = "onEmailInput" in props ? props.onEmailInput : () => {};
  const onEmailEdit = "onEmailEdit" in props ? props.onEmailEdit : () => {};

  return (
    <div css={{ width: "inherit" }}>
      {/* Adds styling to off-set Ionic's Legacy padding */}
      <label
        htmlFor={id}
        css={{ color: Colors.black, fontWeight: "var(--swing-font-medium)", paddingLeft: 16 }}
      >
        {translate("EMAIL_FIELD_LOGIN_INPUT_PLACEHOLDER")}
      </label>
      <div
        css={{
          display: "flex",
          width: "100%",
          borderBottom: "1px solid",
          borderColor: error ? Colors.red500 : Colors.slate400,
        }}
      >
        <IonInput
          id={id}
          type="email"
          name="email"
          value={email}
          onIonInput={(e: CustomEvent) => {
            onEmailChange(e.detail.value);
          }}
          spellCheck="false"
          readonly={readOnly}
          aria-label={translate("EMAIL_FIELD_LOGIN_INPUT_PLACEHOLDER")}
          inputmode="email"
          css={{ paddingLeft: "16px !important", "--highlight-color-focused": "none" }}
        />
        {readOnly && (
          <button
            type="button"
            onClick={() => onEmailEdit(email)}
            aria-pressed="true"
            aria-label={translate("EMAIL_FIELD_LOGIN_INPUT_ARIA_EDIT_LABEL")}
            css={{ background: "none" }}
          >
            <Icon name="Edit" color={Colors.blue500} />
          </button>
        )}
      </div>
      {error && (
        <ErrorLogin
          app={app}
          buildProfile={buildProfile}
          error={error}
          platform={platform}
          provider={provider}
        />
      )}
    </div>
  );
}
